// ===========================================
// Attendance Summary Cards Component
// ===========================================
// Display key attendance metrics using StatWidget cards

import { Grid } from '@mui/material';
import {
  Assessment as AssessmentIcon,
  EventNote as EventNoteIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  EventAvailable as EventAvailableIcon,
} from '@mui/icons-material';

import { AttendanceReportSummary } from '../../api/reports.api';
import { StatWidget } from '../dashboard/StatWidget';

// ===========================================
// TYPES
// ===========================================

interface AttendanceSummaryCardsProps {
  summary: AttendanceReportSummary;
  loading?: boolean;
}

// ===========================================
// COMPONENT
// ===========================================

export default function AttendanceSummaryCards({
  summary,
  loading = false,
}: AttendanceSummaryCardsProps) {
  return (
    <Grid container spacing={2} sx={{ mb: 3 }}>
      {/* Attendance Rate - Primary metric */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Attendance Rate"
          value={`${summary.attendanceRate.toFixed(1)}%`}
          icon={<AssessmentIcon />}
          color={summary.attendanceRate >= 80 ? 'success' : summary.attendanceRate >= 60 ? 'warning' : 'error'}
          subtitle={`${summary.presentCount} / ${summary.totalAttendanceRecords}`}
          loading={loading}
        />
      </Grid>

      {/* Total Records */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Total Records"
          value={summary.totalAttendanceRecords}
          icon={<EventNoteIcon />}
          color="info"
          subtitle={`${summary.totalLessons} lessons`}
          loading={loading}
        />
      </Grid>

      {/* Present Count */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Present"
          value={summary.presentCount}
          icon={<CheckCircleIcon />}
          color="success"
          subtitle={summary.totalAttendanceRecords > 0
            ? `${((summary.presentCount / summary.totalAttendanceRecords) * 100).toFixed(1)}%`
            : '0%'
          }
          loading={loading}
        />
      </Grid>

      {/* Absent Count */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Absent"
          value={summary.absentCount}
          icon={<CancelIcon />}
          color="error"
          subtitle={summary.totalAttendanceRecords > 0
            ? `${((summary.absentCount / summary.totalAttendanceRecords) * 100).toFixed(1)}%`
            : '0%'
          }
          loading={loading}
        />
      </Grid>

      {/* Excused Count */}
      <Grid item xs={12} sm={6} md={2.4}>
        <StatWidget
          title="Excused"
          value={summary.excusedCount}
          icon={<EventAvailableIcon />}
          color="warning"
          subtitle={summary.totalAttendanceRecords > 0
            ? `${((summary.excusedCount / summary.totalAttendanceRecords) * 100).toFixed(1)}%`
            : '0%'
          }
          loading={loading}
        />
      </Grid>
    </Grid>
  );
}
